import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { useCompanies } from '../../../hooks/useFinancialOps';
import { useCreditCards, useCardInvoices } from '../hooks/useCardInvoices';
import { ImportarFaturaButton } from '../components/ImportarFaturaButton';
import { FaturaOrigemBadges } from '../components/FaturaOrigemBadges';
import { formatCurrency, formatDateBR } from '../../../utils/format';
import type { CardInvoiceDTO, CreditCardDTO } from '../services/card-invoices.service';

const TONE: Record<string, 'neutral' | 'accent' | 'warning' | 'danger'> = {
  PENDING: 'warning',
  PROCESSING: 'warning',
  EXTRACTED: 'warning',
  RECONCILING: 'warning',
  CLOSED: 'accent',
  FAILED: 'danger',
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  PROCESSING: 'Processando',
  EXTRACTED: 'Em revisão',
  RECONCILING: 'Aguardando conciliação',
  CLOSED: 'Conciliada',
  FAILED: 'Falhou',
};

export function FaturasListPage() {
  const { data: companiesData } = useCompanies({ active: true });
  const companies = companiesData?.data ?? [];
  const [companyId, setCompanyId] = useState('');
  const { data: cardsData } = useCreditCards(companyId);
  const cards: CreditCardDTO[] = cardsData?.data ?? [];
  const [cardId, setCardId] = useState('');
  const { data, isLoading } = useCardInvoices(cardId);
  const invoices: CardInvoiceDTO[] = data?.data ?? [];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-semibold">Faturas de cartão</h1>
        <ImportarFaturaButton companyId={companyId} />
      </div>
      <div className="flex gap-2 flex-wrap">
        <select
          className="border rounded px-2 py-1"
          value={companyId}
          onChange={(e) => {
            setCompanyId(e.target.value);
            setCardId('');
          }}
        >
          <option value="">Selecione a empresa…</option>
          {companies.map((c: any) => (
            <option key={c.id} value={c.id}>
              {c.tradeName || c.name}
            </option>
          ))}
        </select>
        <select
          className="border rounded px-2 py-1"
          value={cardId}
          onChange={(e) => setCardId(e.target.value)}
          disabled={!companyId}
        >
          <option value="">Selecione o cartão…</option>
          {cards.map((c) => (
            <option key={c.id} value={c.id}>
              {c.issuer} — {c.name}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <LoadingSpinner />}

      {!isLoading && cardId && invoices.length === 0 && (
        <p className="text-ink-secondary">Nenhuma fatura importada para este cartão.</p>
      )}

      {invoices.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-ink-tertiary">
            <tr>
              <th>Competência</th>
              <th>Arquivo</th>
              <th className="text-right pr-2">Total</th>
              <th className="text-right pr-2">Delta</th>
              <th>Importada em</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-border">
                <td className="py-2">
                  {String(inv.referenceMonth).padStart(2, '0')}/{inv.referenceYear}
                </td>
                <td>
                  <span className="inline-flex items-center gap-2">
                    {inv.fileName}
                    <FaturaOrigemBadges invoice={inv} />
                  </span>
                </td>
                <td className="text-right pr-2">{formatCurrency(inv.totalAmount)}</td>
                <td className="text-right pr-2">
                  {inv.reconciledDelta === null ? '—' : formatCurrency(inv.reconciledDelta)}
                </td>
                <td>{formatDateBR(inv.createdAt)}</td>
                <td>
                  <Badge tone={TONE[inv.status] ?? 'neutral'}>
                    {STATUS_LABELS[inv.status] ?? inv.status}
                  </Badge>
                </td>
                <td>
                  <Link to={`/financeiro/faturas/${inv.id}`} className="text-link">
                    Abrir
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
